"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PageHeader() {
  const headlineWords = [{
    text: "Precision",
    italic: false,
    accent: false
  }, {
    text: "in",
    italic: false,
    accent: false
  }, {
    text: "every",
    italic: true,
    accent: false
  }, {
    text: "mark,",
    italic: false,
    accent: false
  }, {
    text: "permanence",
    italic: false,
    accent: false
  }, {
    text: "in",
    italic: false,
    accent: false
  }, {
    text: "every",
    italic: false,
    accent: false
  }, {
    text: "detail\xA0",
    italic: true,
    accent: true
  }, {
    text: "for",
    italic: false,
    accent: false
  }, {
    text: "industry.",
    italic: false,
    accent: false
  }];
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [headlineMounted, setHeadlineMounted] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setHeadlineMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const parallaxOffset = Math.min(scrollY * 0.15, 40);
  return <section data-section-id="2778" id="page-header" className="bg-background text-foreground relative overflow-hidden min-h-[110vh] flex items-center">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-x-4 md:gap-x-6 gap-y-12 md:gap-y-16">
          {}
          <div ref={labelRef} className={`col-span-12 md:col-span-2 transition-all duration-1000 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-[var(--chart-1)]">
              § 00 — industrial marking
            </p>
          </div>

          {}
          <div className="col-span-12 md:col-span-9 md:col-start-2">
            <h1 className="font-default font-light text-foreground" style={{
            fontSize: "clamp(48px, 11vw, 168px)",
            lineHeight: "0.92",
            letterSpacing: "-0.02em"
          }}>
              {headlineWords.map((word, i) => <span data-index={i} key={i} className="inline-block overflow-hidden align-bottom mr-[0.18em]">
                  <span className={`inline-block transition-transform ease-out ${word.italic ? "italic" : ""}`} style={{
                transform: headlineMounted ? "translateY(0%)" : "translateY(110%)",
                transitionDuration: "700ms",
                transitionDelay: `${i * 60}ms`,
                color: word.accent ? "var(--chart-1)" : undefined
              }}>
                    {word.text}
                  </span>
                </span>)}
            </h1>
          </div>

          {}
          <div ref={paragraphRef} className={`col-span-12 md:col-span-4 md:col-start-2 transition-all duration-1000 ease-out delay-300 ${paragraphVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          maxWidth: "320px"
        }}>
            <p className="font-default italic text-[17px] leading-relaxed text-[var(--chart-5)] mb-4">
              Industrial engraving solutions engineered for durability and compliance —
            </p>
            <p className="font-default text-sm leading-relaxed text-muted-foreground mb-8">
              from high-precision laser etching on metal to custom phenolic identification tags for critical infrastructure.
            </p>
            <Link to="/ServicesPage">
              <Button size="lg" className="rounded-lg bg-[var(--chart-1)] text-primary-foreground hover:bg-[var(--chart-1)]/90 px-7 h-12 font-default text-sm tracking-wide shadow-md">
                View Our Services
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {}
          <div ref={imageRef} className={`col-span-12 md:col-span-4 md:col-start-9 transition-all duration-1000 ease-out delay-500 ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transform: imageVisible ? `translateY(${-parallaxOffset}px)` : "translateY(2rem)"
        }}>
            <div className="relative rounded-lg overflow-hidden shadow-lg aspect-[3/4] bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_precision.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_precision.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_precision.png-wvc-sizes" alt="Close-up of industrial laser engraving on stainless steel surface" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(ellipse at center, transparent 50%, hsla(216, 40%, 18%, 0.45) 100%)"
            }} />
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-[var(--chart-5)] mt-4">STANDARD / INDUSTRIAL / 2026</p>
          </div>
        </div>
      </div>

      {}
      <div aria-hidden="true" className="absolute left-0 right-0 bottom-0 h-px bg-border" />
    </section>;
}